import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, siteConfig, categoryThumbnails } from '@/mocks/siteData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const useLightText = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navTextClass = useLightText
    ? 'text-white/90 hover:text-white'
    : 'text-foreground-700 hover:text-primary-500';

  const guideArticles = [
    { title: 'Complete Guide', slug: '/mulebuy-spreadsheet-complete-guide' },
    { title: 'Beginner Tutorial', slug: '/mulebuy-spreadsheet-beginner-tutorial' },
    { title: 'Pricing Guide', slug: '/mulebuy-spreadsheet-pricing-guide' },
    { title: 'Safety Tips', slug: '/mulebuy-spreadsheet-safety-tips' },
    { title: 'vs Traditional Shopping', slug: '/mulebuy-spreadsheet-vs-traditional-shopping' },
    { title: 'Best Practices 2026', slug: '/mulebuy-spreadsheet-best-practices-2026' },
    { title: 'Tools Comparison', slug: '/mulebuy-spreadsheet-tools-comparison' },
    { title: 'Bulk Ordering Guide', slug: '/mulebuy-spreadsheet-bulk-ordering' },
    { title: 'FAQ & Troubleshooting', slug: '/mulebuy-spreadsheet-faq-troubleshooting' },
    { title: 'Mobile Shopping Guide', slug: '/mulebuy-spreadsheet-mobile-guide' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200'
          : 'bg-black/20 backdrop-blur-sm'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className={`flex items-center gap-2 transition-colors ${useLightText ? 'text-white' : 'text-foreground-950 hover:text-primary-500'}`}
          >
            <span className="text-lg md:text-xl font-heading font-semibold tracking-tight">
              {siteConfig.siteName}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}>
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuideDropdownOpen(false); }}
                className={`flex items-center gap-1 text-sm transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200 shadow-lg py-2 z-50 max-h-[70vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={categoryThumbnails[cat.seq]}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => { setGuideDropdownOpen(!guideDropdownOpen); setDropdownOpen(false); }}
                className={`flex items-center gap-1 text-sm transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200 shadow-lg py-2 z-50 max-h-[60vh] overflow-y-auto">
                  {guideArticles.map((guide) => (
                    <Link
                      key={guide.slug}
                      to={guide.slug}
                      onClick={() => setGuideDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {guide.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href={siteConfig.mainSite}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm bg-primary-500 text-background-50 px-5 py-2 rounded-md font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${useLightText ? 'text-white' : 'text-foreground-700'}`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/30">
            <div className="flex flex-col gap-0.5 pt-3">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2.5 text-sm rounded-md transition-colors flex items-center gap-2 cursor-pointer ${useLightText ? 'text-white/90 hover:bg-white/10' : 'text-foreground-700 hover:bg-background-100'}`}
              >
                <i className="ri-home-4-line text-base"></i>
                Home
              </Link>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className={`px-3 py-2.5 text-sm font-medium rounded-md transition-colors flex items-center justify-between w-full cursor-pointer ${useLightText ? 'text-white/90 hover:bg-white/10' : 'text-foreground-700 hover:bg-background-100'}`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line text-base"></i>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="pl-2 pr-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className={`flex items-center gap-3 px-4 py-2 text-sm rounded-md transition-colors cursor-pointer ${useLightText ? 'text-white/80 hover:bg-white/10' : 'text-foreground-600 hover:bg-background-100'}`}
                    >
                      <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={categoryThumbnails[cat.seq]}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}

              <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider mt-1 ${useLightText ? 'text-white/50' : 'text-foreground-400'}`}>
                Shopping Guides
              </div>
              {guideArticles.map((guide) => (
                <Link
                  key={guide.slug}
                  to={guide.slug}
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-2 text-sm rounded-md transition-colors cursor-pointer ${useLightText ? 'text-white/80 hover:bg-white/10' : 'text-foreground-600 hover:bg-background-100'}`}
                >
                  {guide.title}
                </Link>
              ))}

              <a
                href={siteConfig.mainSite}
                target="_blank"
                rel="noopener nofollow"
                className="mx-3 mt-3 text-sm bg-primary-500 text-background-50 px-5 py-2.5 rounded-md font-medium text-center hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}